import type {
    CommerceCart,
    CommerceCartCoupon,
    CommerceCartLine,
    CommerceCartTotals,
    CommerceImage,
    CommerceMoney,
    CommerceProductComponent,
} from "@teqtank/commerce-contracts";
import { CommerceError } from "../errors/commerce-error.js";
import { resolveRecurringFrequencyOption } from "../products/product-response-resolver.js";

export function resolveCommerceCart(value: CommerceCart | null | undefined): CommerceCart {
    if (value === null || value === undefined || typeof value !== "object") {
        throw invalid("Commerce Cart response was missing.");
    }

    const currencyCode = requireCurrency(value.currencyCode, "cart.currencyCode");
    const lines = requireArray(value.lines, "cart.lines").map((line) =>
        resolveLine(line, currencyCode),
    );
    const coupons = requireArray(value.coupons, "cart.coupons").map((coupon) =>
        resolveCoupon(coupon, currencyCode),
    );

    return Object.freeze({
        id: requireString(value.id, "cart.id"),
        version: requireNonNegativeInteger(value.version, "cart.version"),
        currencyCode,
        lines: Object.freeze(lines),
        coupons: Object.freeze(coupons),
        totals: resolveTotals(value.totals, currencyCode),
        updatedAt: resolveDate(value.updatedAt, "cart.updatedAt"),
    });
}

function resolveLine(value: CommerceCartLine, currency: string): CommerceCartLine {
    return Object.freeze({
        id: requireString(value.id, "cartLine.id"),
        productId: requireString(value.productId, "cartLine.productId"),
        variantId: requireString(value.variantId, "cartLine.variantId"),
        ...(value.displaySku == null
            ? {}
            : {
                  displaySku: requireString(value.displaySku, "cartLine.displaySku"),
              }),
        name: requireString(value.name, "cartLine.name"),
        image: value.image === null ? null : resolveImage(value.image),
        purchaseType:
            value.purchaseType === "one-time" || value.purchaseType === "recurring"
                ? value.purchaseType
                : fail("cartLine.purchaseType must be one-time or recurring."),
        ...(value.frequency == null
            ? {}
            : {
                  frequency: resolveRecurringFrequencyOption(value.frequency, "cartLine.frequency"),
              }),
        ...(value.frequencyDays == null ? {} : { frequencyDays: value.frequencyDays }),
        quantity: requirePositiveInteger(value.quantity, "cartLine.quantity"),
        unitPrice: resolveMoney(value.unitPrice, currency, "cartLine.unitPrice"),
        lineSubtotal: resolveMoney(value.lineSubtotal, currency, "cartLine.lineSubtotal"),
        lineDiscount: resolveMoney(value.lineDiscount, currency, "cartLine.lineDiscount"),
        lineTotal: resolveMoney(value.lineTotal, currency, "cartLine.lineTotal"),
        ...(value.components == null
            ? {}
            : {
                  components: Object.freeze(
                      requireArray(value.components, "cartLine.components").map(
                          (component, index) =>
                              resolveComponent(component, `cartLine.components[${index}]`),
                      ),
                  ),
              }),
    });
}

function resolveComponent(
    value: CommerceProductComponent,
    property: string,
): CommerceProductComponent {
    return Object.freeze({
        productId: requireString(value.productId, `${property}.productId`),
        ...(value.displaySku == null
            ? {}
            : { displaySku: requireString(value.displaySku, `${property}.displaySku`) }),
        name: requireString(value.name, `${property}.name`),
        image: value.image === null ? null : resolveImage(value.image),
        quantity: requirePositiveInteger(value.quantity, `${property}.quantity`),
        ...(value.components == null
            ? {}
            : {
                  components: Object.freeze(
                      requireArray(value.components, `${property}.components`).map(
                          (component, index) =>
                              resolveComponent(component, `${property}.components[${index}]`),
                      ),
                  ),
              }),
    });
}

function resolveCoupon(value: CommerceCartCoupon, currency: string): CommerceCartCoupon {
    return Object.freeze({
        code: requireString(value.code, "cartCoupon.code"),
        description: requireString(value.description, "cartCoupon.description"),
        discount: resolveMoney(value.discount, currency, "cartCoupon.discount"),
    });
}

function resolveTotals(value: CommerceCartTotals, currency: string): CommerceCartTotals {
    if (value === null || value === undefined || typeof value !== "object")
        fail("cart.totals was missing.");
    return Object.freeze({
        itemCount: requireNonNegativeInteger(value.itemCount, "cart.totals.itemCount"),
        subtotal: resolveMoney(value.subtotal, currency, "cart.totals.subtotal"),
        discount: resolveMoney(value.discount, currency, "cart.totals.discount"),
        shipping: resolveMoney(value.shipping, currency, "cart.totals.shipping"),
        tax: resolveMoney(value.tax, currency, "cart.totals.tax"),
        total: resolveMoney(value.total, currency, "cart.totals.total"),
    });
}

function resolveMoney(value: CommerceMoney, currency: string, property: string): CommerceMoney {
    if (
        value === null ||
        value === undefined ||
        typeof value !== "object" ||
        typeof value.amount !== "number" ||
        !Number.isFinite(value.amount) ||
        value.amount < 0
    )
        fail(`${property} was invalid.`);
    if (requireCurrency(value.currencyCode, `${property}.currencyCode`) !== currency)
        fail(`${property} currency did not match the Cart currency.`);
    return Object.freeze({
        amount: value.amount,
        currencyCode: currency,
        formatted: requireString(value.formatted, `${property}.formatted`),
    });
}

function resolveImage(value: CommerceImage): CommerceImage {
    const width =
        value.width == null ? undefined : requirePositiveInteger(value.width, "image.width");
    const height =
        value.height == null ? undefined : requirePositiveInteger(value.height, "image.height");
    return Object.freeze({
        url: requireString(value.url, "image.url"),
        alternativeText:
            typeof value.alternativeText === "string"
                ? value.alternativeText.trim()
                : fail("image.alternativeText must be a string."),
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
    });
}

function resolveDate(value: Date, property: string): Date {
    const date =
        value instanceof Date ? new Date(value.getTime()) : new Date(value as unknown as string);
    if (Number.isNaN(date.getTime())) fail(`${property} must be a valid date.`);
    return Object.freeze(date);
}
function requireString(value: unknown, property: string): string {
    if (typeof value !== "string" || value.trim().length === 0)
        fail(`${property} must be a non-empty string.`);
    return value.trim();
}
function requireCurrency(value: unknown, property: string): string {
    const result = requireString(value, property).toUpperCase();
    if (!/^[A-Z]{3}$/u.test(result)) fail(`${property} must be a valid currency code.`);
    return result;
}
function requirePositiveInteger(value: unknown, property: string): number {
    if (!Number.isInteger(value) || Number(value) < 1)
        fail(`${property} must be an integer greater than zero.`);
    return Number(value);
}
function requireNonNegativeInteger(value: unknown, property: string): number {
    if (!Number.isInteger(value) || Number(value) < 0)
        fail(`${property} must be a non-negative integer.`);
    return Number(value);
}
function requireArray<T>(value: readonly T[], property: string): readonly T[] {
    if (!Array.isArray(value)) fail(`${property} must be an array.`);
    return value;
}
function invalid(message: string): CommerceError {
    return new CommerceError("cart-response-invalid", message);
}
function fail(message: string): never {
    throw invalid(message);
}
